// "Upload a book" — the full-page pressed-theme capture flow.
//   ONE capture flow, TWO landings by intent (store `uploadEntry`):
//     library → the shipped Book Analysis page (book on the shelf, scored)
//     home    → watch the games build → DoneHome → the set canvas
// Steps: add → added → details → analyzing (read pages, then analyze text)
//        → found → [home only: building → done]. See
//        specs/design_handoff_upload-July8.
//
// State + handlers live in useUploadFlow (src/hooks) — this file is the
// presentation layer only. Styling lives in styles/upload-flow.css.

import { useRef, useState, type CSSProperties } from 'react';
import '@/styles/upload-flow.css';
import { cn } from '@/lib/utils';
import { useBookAnalysis } from '@/hooks/useBookAnalysis';
import { useUploadFlow, type UploadPage } from '@/hooks/useUploadFlow';
import { useGlobalState } from '@/stores/globalState';
import type { Book } from '@/types';
import {
  UploadShell,
  GhostBtn,
  CoralBtn,
  FewerTip,
  Field,
  type UploadStepId } from
'./UploadChrome';import { CssVarDiv } from "@/components/CssVarElements";

export function UploadFlowPage() {
  const flow = useUploadFlow();
  if (!flow.entry) return null;
  // Re-passing `entry` explicitly (rather than relying on the spread) keeps
  // TS's narrowing: `{...flow}` alone widens `entry` back to
  // `'library' | 'home' | null` since the spread reads flow's declared
  // type, not the narrowed one from the guard above.
  return <Body {...flow} entry={flow.entry} />;
}

// ── The step bodies + footer wiring (kept in one place for shared state) ─────

interface BodyProps {
  entry: 'library' | 'home';
  step: UploadStepId;
  backLabel: string;
  onClose: () => void;
  pages: UploadPage[];
  title: string;
  author: string;
  isChapterBook: boolean;
  chapterLabel: string;
  error: string | null;
  ocr: {completed: number;total: number;failed: number;};
  analysisPhase: 'reading' | 'analyzing';
  bookId: string | null;
  book: Book | null;
  onAddFiles: (files: FileList | File[]) => void;
  onRemovePage: (id: string) => void;
  setTitle: (v: string) => void;
  setAuthor: (v: string) => void;
  setIsChapterBook: (v: boolean) => void;
  setChapterLabel: (v: string) => void;
  setStep: (s: UploadStepId) => void;
  onAnalyze: () => void;
  onSeeAnalysis: () => void;
}

function Body(p: BodyProps) {

  let heading: string | undefined;
  let sub: string | undefined;
  let center = false;
  let footer: React.ReactNode = null;
  let body: React.ReactNode = null;

  if (p.step === 'add' || p.step === 'added') {
    heading = 'Add the book’s pages';
    sub = 'Photograph the pages, or upload photos you already have.';
    body =
    <div className="upload-flow__step-body">
        {p.error && <ErrorNote>{p.error}</ErrorNote>}
        <AddPages pages={p.pages} onAddFiles={p.onAddFiles} onRemove={p.onRemovePage} />
        <FewerTip />
      </div>;

    footer =
    <>
        <GhostBtn onClick={p.onClose}>Cancel</GhostBtn>
        <CoralBtn disabled={p.pages.length === 0} arrow onClick={() => p.setStep('details')}>Continue</CoralBtn>
      </>;

  } else if (p.step === 'details') {
    heading = 'A few quick details';
    sub = 'So we can label the set and line the patterns up.';
    body =
    <div className="upload-flow__step-body upload-flow__step-body--details">
        {p.error && <ErrorNote>{p.error}</ErrorNote>}
        <div className="upload-flow__pages-ready-row">
          <div className="upload-flow__mini-stack">
            {[0, 1, 2].map((i) =>
          <div key={i} className="upload-flow__mini-page" />
          )}
          </div>
          <span className="upload-flow__pages-ready-label">
            {p.pages.length} page{p.pages.length !== 1 ? 's' : ''} ready. Just a couple of details.
          </span>
        </div>
        <Field label="Book title" value={p.title} onChange={p.setTitle} placeholder="e.g. Heidi's Tree Trouble" />
        <Field label="Author" value={p.author} onChange={p.setAuthor} placeholder="e.g. Sarah Weeks" optional />
        <div className="upload-flow__field-group">
          <label className="upload-flow__field-label">Is this from a chapter book?</label>
          <div className="upload-flow__toggle-row">
            <Toggle active={!p.isChapterBook} onClick={() => p.setIsChapterBook(false)}>No, it&rsquo;s one story</Toggle>
            <Toggle active={p.isChapterBook} onClick={() => p.setIsChapterBook(true)}>Yes, a chapter book</Toggle>
          </div>
        </div>
        {p.isChapterBook &&
      <div className="upload-flow__field-group--tight">
            <label className="upload-flow__field-label">Which chapter are these pages from?</label>
            <Field label="" value={p.chapterLabel} onChange={p.setChapterLabel} placeholder="e.g. Chapter 4" />
            <span className="upload-flow__field-hint">Just the chapter these pages came from, so we can line the patterns up.</span>
          </div>
      }
      </div>;

    footer =
    <>
        <GhostBtn onClick={() => p.setStep(p.pages.length ? 'added' : 'add')}>&larr; Back</GhostBtn>
        <CoralBtn disabled={!p.title.trim()} arrow onClick={p.onAnalyze}>Analyze book</CoralBtn>
      </>;

  } else if (p.step === 'analyzing') {
    center = true;
    body = <Analyzing title={p.title} ocr={p.ocr} phase={p.analysisPhase} error={p.error} />;
  } else if (p.step === 'found') {
    heading = 'Here’s what we found';
    sub = `A quick summary from ${p.title || 'your book'}. Open the analysis for the full picture.`;
    body = <FoundSummary bookId={p.bookId} book={p.book} />;
    // Both intents land on the real Book Analysis page — the parent reviews
    // the full fit report and generates the games from there (the CTA there
    // swaps to the live blueprint, then a games-ready confirmation).
    footer =
    <>
        <GhostBtn onClick={p.onClose}>Close</GhostBtn>
        <CoralBtn arrow onClick={p.onSeeAnalysis}>See book analysis</CoralBtn>
      </>;

  }

  return (
    <UploadShell step={p.step} backLabel={p.backLabel} onClose={p.onClose} heading={heading} sub={sub} center={center} footer={footer}>
      {body}
    </UploadShell>);

}

// ── small pieces ─────────────────────────────────────────────────────────────

function ErrorNote({ children }: {children: React.ReactNode;}) {
  return (
    <div className="upload-flow__error-note">
      {children}
    </div>);

}

function Toggle({ active, onClick, children }: {active: boolean;onClick: () => void;children: React.ReactNode;}) {
  return (
    <button
      type="button" onClick={onClick}
      className={cn('upload-flow__toggle', active && 'upload-flow__toggle--active')}>

      {active && <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="var(--yellow)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11l4 4 8-9" /></svg>}
      {children}
    </button>);

}

// Owns its own file-input ref so the ref never crosses a prop boundary
// (react-compiler forbids reading a ref passed through props during render).
function AddPages({ pages, onAddFiles, onRemove }: {pages: UploadPage[];onAddFiles: (f: FileList | File[]) => void;onRemove: (id: string) => void;}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const pick = () => inputRef.current?.click();
  return (
    <>
      {pages.length === 0 ?
      <DropZone onPick={pick} onDropFiles={onAddFiles} /> :
      <PagesGrid pages={pages} onRemove={onRemove} onAdd={pick} />}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,.heic,.heif,.pdf,image/jpeg,image/png,image/heic,image/heif,application/pdf"
        onChange={(e) => {
          if (e.target.files) onAddFiles(e.target.files);
          e.target.value = '';
        }}
        className="upload-flow__hidden-input" />

    </>);

}

function DropZone({ onPick, onDropFiles }: {onPick: () => void;onDropFiles: (f: FileList) => void;}) {
  const [drag, setDrag] = useState(false);
  return (
    <div
      onClick={onPick}
      onDragOver={(e) => {e.preventDefault();setDrag(true);}}
      onDragLeave={(e) => {e.preventDefault();setDrag(false);}}
      onDrop={(e) => {e.preventDefault();setDrag(false);if (e.dataTransfer.files.length) onDropFiles(e.dataTransfer.files);}}
      className={cn('upload-flow__drop-zone', drag && 'upload-flow__drop-zone--drag')}>

      <span className="upload-flow__drop-zone-icon">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 16V6m0 0l-4 4m4-4l4 4M4 18h16" /></svg>
      </span>
      <div className="upload-flow__drop-zone-title">Drag pages here</div>
      <div className="upload-flow__drop-zone-sub">Photos or scans of the pages. JPG, PNG, or PDF.</div>
      <div className="upload-flow__drop-zone-actions">
        <span className="hs-press hs-press--paper upload-flow__pill upload-flow__pill--primary">Take photos</span>
        <span className="hs-press hs-press--paper upload-flow__pill">Choose files</span>
      </div>
    </div>);

}

function PagesGrid({ pages, onRemove, onAdd }: {pages: UploadPage[];onRemove: (id: string) => void;onAdd: () => void;}) {
  const enough = pages.length >= 5;
  return (
    <div className="upload-flow__step-body">
      <div className="upload-flow__pages-grid-header">
        <div className="upload-flow__pages-count">{pages.length} page{pages.length !== 1 ? 's' : ''} added</div>
        {enough &&
        <span className="upload-flow__good-amount">
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="var(--green)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11l4 4 8-9" /></svg>
            A good amount
          </span>
        }
      </div>
      <div className="upload-flow__pages-grid">
        {pages.map((pg, i) =>
        <div key={pg.id} className="upload-flow__page-thumb">
            {pg.file.type === 'application/pdf' ?
          <div className="upload-flow__page-thumb-pdf">PDF</div> :
          <img src={pg.previewUrl} alt="" className="upload-flow__page-thumb-img" />}
            <span className="upload-flow__page-number">{i + 1}</span>
            <button type="button" onClick={() => onRemove(pg.id)} aria-label="Remove page" className="upload-flow__page-remove">
              <svg width="10" height="10" viewBox="0 0 20 20" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round"><path d="M5 5l10 10M15 5L5 15" /></svg>
            </button>
          </div>
        )}
        <button type="button" onClick={onAdd} className="upload-flow__page-add">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round" opacity="0.55"><path d="M10 5v10M5 10h10" /></svg>
          <span className="upload-flow__page-add-label">Add</span>
        </button>
      </div>
    </div>);

}

function Analyzing({ title, ocr, phase, error }: {title: string;ocr: {completed: number;total: number;failed: number;};phase: 'reading' | 'analyzing';error: string | null;}) {
  const total = ocr.total || 1;
  const pct = Math.round(ocr.completed / total * 100);
  const analyzing = phase === 'analyzing';
  const rows: [string, boolean][] = [
  ['Extracting the text', analyzing],
  ['Sounding out the words', analyzing],
  ['Finding the patterns worth practicing', false]];

  return (
    <div className="upload-flow__analyzing">
      <div className="upload-flow__analyzing-icon">
        <div className="upload-flow__analyzing-icon-back" />
        <div className="upload-flow__analyzing-icon-front">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="var(--coral)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="10.5" cy="10.5" r="6.5" /><path d="M15 15l4.5 4.5" /></svg>
        </div>
      </div>
      <div>
        <div className="upload-flow__analyzing-heading">Reading {title || 'your book'}&hellip;</div>
        {/* DEP-192: OCR can now escalate to a vision read (~a minute per book),
             so set honest expectations during the `reading` phase; analysing is
             quick. Progress bar below advances per page as they complete. */}
        <div className="upload-flow__analyzing-sub">
          {analyzing ?
          'Almost there — analysing the words.' :
          'We read each page carefully — this can take a minute or two.'}
        </div>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="upload-flow__progress">
        <div className="upload-flow__progress-label-row">
          <span className="upload-flow__eyebrow">Reading pages</span>
          <span className="upload-flow__progress-count">{ocr.completed}<span className="upload-flow__progress-count-total"> / {ocr.total}</span></span>
        </div>
        <div className="upload-flow__progress-track">
          <CssVarDiv className="upload-flow__progress-fill" cssVars={{ '--upload-progress': `${pct}%` } as CSSProperties} />
        </div>
        <div className="upload-flow__progress-ticks">
          {Array.from({ length: ocr.total }).map((_, i) =>
          <span key={i} className={cn('upload-flow__progress-tick', i < ocr.completed && 'upload-flow__progress-tick--done')} />
          )}
        </div>
      </div>
      <div className={cn('upload-flow__checklist', !analyzing && 'upload-flow__checklist--dim')}>
        {rows.map(([label, done], idx) => {
          const active = analyzing && !done && (idx === 0 || rows[idx - 1][1]);
          return (
            <div key={idx} className="upload-flow__checklist-row">
              <span className={cn('upload-flow__check-bubble', done && 'upload-flow__check-bubble--done')}>
                {done ? <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11l4 4 8-9" /></svg> :
                <span className={cn('upload-flow__check-dot', active && 'upload-flow__check-dot--active')} />}
              </span>
              <span className={cn('upload-flow__check-label', done && 'upload-flow__check-label--done')}>{label}</span>
            </div>);

        })}
      </div>
    </div>);

}

function FoundSummary({ bookId, book }: {bookId: string | null;book: Book | null;}) {
  const learnerStage = useGlobalState((s) => s.activeLearner?.readingStage) ?? null;
  const hasStage = typeof learnerStage === 'number' && learnerStage >= 1 && learnerStage <= 5;
  const { data: analysis } = useBookAnalysis(bookId, hasStage ? learnerStage : null);

  const uniqueWords = book?.bookStats?.unique_word_count ?? null;
  const richVocab = analysis?.vocabulary?.length ?? null;

  const rows: string[] = [
  'Text extracted',
  uniqueWords != null ? `${uniqueWords.toLocaleString()} unique words found` : 'Unique words counted',
  'Phonics patterns detected',
  'High-frequency words identified',
  ...(richVocab ? [`Rich vocabulary detected (${richVocab} words)`] : []),
  'Reading features analyzed'];

  return (
    <div className="upload-flow__found-outer">
      <div className="upload-flow__found-list">
        {rows.map((label) =>
        <div key={label} className="upload-flow__found-row">
            <span className="upload-flow__found-check">
              <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11l4 4 8-9" /></svg>
            </span>
            <span className="upload-flow__found-label">{label}</span>
          </div>
        )}
      </div>
      <div className="upload-flow__found-note">
        <span className="upload-flow__found-note-dot" />
        The full breakdown, with patterns, word lists and reading level, is on the book analysis.
      </div>
    </div>);

}